import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Typography,
  Menu,
  MenuItem,
  IconButton,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/core';
import { JsonEditor as Editor } from 'jsoneditor-react';
import EditIcon from '@material-ui/icons/Edit';
import { useRootStore } from '../../../stores/BridgeProvider';
import { ButtonFlex, WideBox, FlexDiv } from '../../styles/styles';
import Spinner from '../../util/Spinner';
import 'jsoneditor-react/es/editor.min.css';
import TemplatePreview from './TemplatePreview/TemplatePreview';
import DealPreview from './DealPreview/DealPreview';
import RenderPropModal from '../../dealMapper/common/RenderPropModal';
import NameModal from './NameModal';

const EditorStyles = css`
  .jsoneditor {
    min-height: 600px !important;
    textarea {
      min-height: 600px;
    }
  }
  #JSONEditorContainer {
    width: 100%;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;

const JsonEditor = ({ data = {}, ...rest }) => {
  const jsonEditorRef = useRef(null);

  useEffect(() => {
    if (jsonEditorRef.current !== null) {
      jsonEditorRef.current.set(data);
    }
  }, [data]);

  const setRef = instance => {
    if (instance) {
      jsonEditorRef.current = instance.jsonEditor;
    } else {
      jsonEditorRef.current = null;
    }
  };

  return <Editor ref={setRef} value={data} {...rest} />;
};

const DealTemplate = () => {
  const history = useHistory();
  const { templateId } = useParams();
  const { authStore, dataStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [isPullingItems, setIsPullingItems] = useState(true);
  const [originalDealTemplate, setOriginalDealTemplate] = useState({});
  const [dealTemplate, setDealTemplate] = useState(null);
  const [showJSON, setShowJON] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const transactionCategories = dataStore.validTransactionCategoryArray;
  const { contacts } = dataStore;
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleJSONEditor = () => {
    setShowJON(!showJSON);
  };

  const initializeTemplate = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/deal-template/${templateId}`,
      );
      setOriginalDealTemplate(data);
      const { DealTemplateId, ...rest } = data;
      setDealTemplate(rest);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    initializeTemplate();
  }, [templateId]);

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      await Promise.all(requests);
      setIsPullingItems(false);
    };
    initialize();
  }, []);

  const { DealTemplateId, ...ogDT } = originalDealTemplate;

  const isDirty = useMemo(() => {
    return JSON.stringify(ogDT) !== JSON.stringify(dealTemplate);
  }, [dealTemplate, originalDealTemplate]);

  if (isLoading || isPullingItems || !dealTemplate) return <Spinner />;

  const deleteTemplate = async () => {
    try {
      await authStore.request.delete(
        `${process.env.REACT_APP_API}/api/deal-template/`,
        {
          data: { DealTemplateId },
        },
      );
      history.push('/deal-templates');
    } catch (e) {
      console.error(e);
    }
  };

  const updateTemplate = async () => {
    try {
      const { Name, Description, TemplateJSON } = dealTemplate;
      const DT_FORMATTED = JSON.stringify(TemplateJSON);
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/deal-template/`,
        {
          DealTemplateId,
          Template: DT_FORMATTED,
          Name,
          Description,
        },
      );
      setLoading(true);
      initializeTemplate();
    } catch (e) {
      console.error(e);
    }
  };

  const updateTemplateJSON = TemplateJSON => {
    setDealTemplate({ ...dealTemplate, TemplateJSON });
  };

  const updateNameAndDesc = (Name, Description) => {
    setDealTemplate({ ...dealTemplate, Name, Description });
  };

  const { Name, Description, TemplateJSON } = dealTemplate;

  return (
    <FlexDiv>
      <WideBox style={{ minHeight: '0px' }} showoverflow={1}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ margin: '0 auto', paddingLeft: '68px' }}>
            <Typography variant="h2">{Name}</Typography>
            <Typography variant="h4" style={{ marginTop: '10px' }}>
              {Description}
            </Typography>
          </div>

          <RenderPropModal
            cardDisplay={({ handleClose: handleModalClose }) => (
              <NameModal
                handleClose={handleModalClose}
                name={Name}
                description={Description}
                updateTemplate={updateNameAndDesc}
              />
            )}
          >
            {({ handleOpen }) => (
              <IconButton onClick={handleOpen}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </RenderPropModal>
          <IconButton
            className="voucherStep1 voucherStep2"
            onClick={e => handleClick(e)}
            style={{ padding: 0, height: 'fit-content' }}
          >
            <MoreVertIcon />
          </IconButton>
        </div>
      </WideBox>

      <TemplatePreview
        dealTemplate={dealTemplate}
        updateTemplateJSON={updateTemplateJSON}
      />

      <DealPreview
        json={TemplateJSON}
        updateTemplateJSON={updateTemplateJSON}
      />

      <WideBox css={EditorStyles} showoverflow={1} style={{ minHeight: '0px' }}>
        {showJSON && (
          <div id="JSONEditorContainer">
            <JsonEditor
              mode="tree"
              allowedModes={['code', 'tree']}
              onChange={e => setDealTemplate(e)}
              data={dealTemplate}
            />
          </div>
        )}
        <ButtonFlex style={{ marginTop: '0px' }}>
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={updateTemplate}
            disabled={!isDirty}
          >
            Save Changes
          </Button>
          <Button
            variant="contained"
            size="large"
            color="primary"
            disabled={isDirty}
            onClick={() =>
              history.push({
                pathname: '/new-deal-from-template',
                state: { templateId },
              })
            }
          >
            Create deal from template
          </Button>
        </ButtonFlex>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem style={{ marginRight: '0px' }} onClick={deleteTemplate}>
            Delete Template
          </MenuItem>
          <MenuItem
            style={{ marginRight: '0px' }}
            onClick={() => {
              toggleJSONEditor();
              handleClose();
            }}
          >
            {showJSON ? 'Hide' : 'Show'} JSON Editor
          </MenuItem>
        </Menu>
      </WideBox>
    </FlexDiv>
  );
};

export default DealTemplate;
